import React, { useContext, useState, useEffect } from 'react';
import { ProgressBar, Step } from 'react-step-progress-bar';
import 'react-step-progress-bar/styles.css';
import ReactTooltip from 'react-tooltip';
import { withTheme } from 'styled-components';
import { Row, Col } from 'react-flexbox-grid';
import { BiLike } from 'react-icons/bi';
import { GlobalContext } from '../../context/GlobalState';
import S from './style';
import Button from '../Button';
import Flex from '../Flex';
import { SCREENS } from '../../constants';
import * as boardStages from '../../config/boardStages';
import { updateRetrospective } from '../../services/retrospectives';
import { logEvent, TrackingEvents } from '../../tracking';
import Timer from '../Timer';
import { useQueryClient } from '@tanstack/react-query';

function StepBar({ theme }) {
  const [percent, setPercent] = useState(0);
  const [steps, setSteps] = useState([]);
  const { cards, currentRetrospectiveId, retrospective, user, onChangeScreen } =
    useContext(GlobalContext);

  const queryClient = useQueryClient();

  const userVotes = () =>
    cards.reduce((a, c) => (c.votes.includes(user.id) ? a + 1 : a), 0);

  const stages = steps.map((item) => item.id);
  const stageIndex = stages.indexOf(retrospective.stage);

  useEffect(() => {
    const currentSteps =
      retrospective.teamMoodQuestions?.length > 0
        ? boardStages.stepWithMood
        : boardStages.normalStep;

    setSteps(currentSteps);
    const { percent: percentStep } = currentSteps.find(
      (item) => item.id === retrospective.stage
    );
    setPercent(percentStep);
  }, [retrospective]);

  const goToDashboard = async () => {
    await queryClient.invalidateQueries(['getRetrospectives']);
    onChangeScreen(SCREENS.WORKSPACE);
  };

  const handleGoToDashboardClick = () => {
    logEvent(TrackingEvents.GO_BACK_TO_DASHBOARD, {
      stage: retrospective.stage,
    });
    goToDashboard();
  };

  const goToStage = async (stageIndexNavigation) => {
    const idStages = steps.map((item) => item.id);
    const stage = idStages[stageIndexNavigation];
    logEvent(
      stageIndexNavigation > stageIndex
        ? TrackingEvents.MOVE_TO_NEXT_STAGE
        : TrackingEvents.MOVE_TO_PREVIOUS_STAGE,
      { stage }
    );

    await updateRetrospective(currentRetrospectiveId, {
      stage,
      usersDoneWithCurrentStage: [],
    });
  };

  const onFinish = () => {
    logEvent(TrackingEvents.FINISH_RETROSPECTIVE);
    goToDashboard();
  };

  const isFacilitator = () => user.id === retrospective.facilitator;

  const isLimitedVotes = () =>
    retrospective.stage === 'VOTE' && retrospective.votesLimit;

  const nextDisabled = () =>
    (stages.length === 5 && cards.length === 0 && stageIndex <= 1) ||
    (stages.length === 6 && stageIndex === 1 && cards.length === 0);

  const userVotesAvailable = () => {
    const votesAvailable = retrospective.votesLimit - userVotes();
    const withoutVotes = 0;
    if (votesAvailable > 0) {
      return votesAvailable;
    }
    return withoutVotes;
  };

  return (
    <S.Container>
      <Row middle="xs" between="xs">
        <Col xs={2}>
          <S.Title size="sm">{retrospective.name}</S.Title>
          <Button link size="sm" onClick={handleGoToDashboardClick}>
            ← Dashboard
          </Button>
        </Col>

        <Col xs={5}>
          <Flex align="center" column auto style={{ marginTop: '-12px' }}>
            <S.WrapProgress>
              <ProgressBar
                percent={percent}
                unfilledBackground={theme.palette.colorStepbarLine}
                filledBackground={theme.palette.colorStepbarLineActive}
                hasStepZero
                height={6}
              >
                {steps.map((item) => (
                  <Step key={item.percent}>
                    {({ accomplished }) => (
                      <>
                        <S.Step accomplished={accomplished} />
                        <S.StepLabel accomplished={accomplished}>
                          {item.name}
                        </S.StepLabel>
                      </>
                    )}
                  </Step>
                ))}
              </ProgressBar>
            </S.WrapProgress>
          </Flex>
        </Col>

        <Col xs={5}>
          <Flex justify="flex-end" align="center">
            <Timer
              readOnly={!isFacilitator()}
              timerInitialTime={retrospective.timerInitialTime}
              timerStartedAt={retrospective.timerStartedAt}
              timerIsTimerActive={retrospective.timerIsTimerActive}
              retrospectiveId={retrospective.id}
            />
            {isLimitedVotes() && (
              <>
                <S.VoteCount data-tip data-for="votesCount">
                  <BiLike />
                  <S.VoteBadge>
                    {userVotesAvailable() <= 0 ? 0 : userVotesAvailable()}
                  </S.VoteBadge>
                </S.VoteCount>
                <ReactTooltip
                  id="votesCount"
                  place="bottom"
                  effect="solid"
                  border
                >
                  <S.TooltipContent>
                    You have {userVotesAvailable()} votes left
                  </S.TooltipContent>
                </ReactTooltip>
              </>
            )}
            {stageIndex > 0 && isFacilitator() && (
              <Button secondary onClick={() => goToStage(stageIndex - 1)}>
                Back
              </Button>
            )}
            {((stages.length === 5 && stageIndex <= 3 && isFacilitator()) ||
              (stages.length === 6 && stageIndex <= 4 && isFacilitator())) && (
              <span
                data-tip=""
                data-for="nextButton"
                data-tip-disable={!nextDisabled()}
              >
                <Button
                  onClick={() => goToStage(stageIndex + 1)}
                  disabled={nextDisabled()}
                  style={{ marginLeft: 15 }}
                >
                  Next
                </Button>
                <ReactTooltip
                  id="nextButton"
                  place="left"
                  effect="solid"
                  border
                >
                  <S.TooltipContent>
                    At least one idea from any participant is required to move
                    to the next step
                  </S.TooltipContent>
                </ReactTooltip>
              </span>
            )}
            {((stages.length === 5 && stageIndex === 4 && isFacilitator()) ||
              (stages.length === 6 && stageIndex === 5 && isFacilitator())) && (
              <Button onClick={onFinish} style={{ marginLeft: 15 }}>
                Finish
              </Button>
            )}
          </Flex>
        </Col>
      </Row>
    </S.Container>
  );
}

export default withTheme(StepBar);
